import { MessageSquare, Moon, Sun } from 'lucide-react'
import { toggleTheme } from '@/hooks/useTheme'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { buildBreadcrumbs } from './breadcrumb-config'
import type { ShellMissionSummary } from './types'

interface ShellDesktopHeaderProps {
  pathname: string
  selectedMissionId: string | null
  missions: ShellMissionSummary[]
  theme: string
  onOpenAssistant: () => void
  onMissionChange: (missionId: string) => void
}

export function ShellDesktopHeader({
  pathname,
  selectedMissionId,
  missions,
  theme,
  onOpenAssistant,
  onMissionChange,
}: ShellDesktopHeaderProps) {
  const crumbs = buildBreadcrumbs(pathname)

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl">
      <div className="flex h-14 items-center justify-between px-6">
        <Breadcrumb>
          <BreadcrumbList>
            {crumbs.map((crumb, index) => {
              const isLast = index === crumbs.length - 1
              return (
                <BreadcrumbItem key={crumb.href}>
                  {index > 0 && <BreadcrumbSeparator />}
                  {isLast ? (
                    <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink to={crumb.href}>{crumb.label}</BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              )
            })}
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex items-center gap-4">
          <div className="hidden md:block">
            <Select
              value={selectedMissionId || undefined}
              onValueChange={onMissionChange}
              disabled={missions.length === 0}
            >
              <SelectTrigger className="h-8 w-[280px]">
                <SelectValue placeholder="Select mission..." />
              </SelectTrigger>
              <SelectContent>
                {missions.map((mission) => (
                  <SelectItem key={mission.mission_id} value={mission.mission_id}>
                    {mission.mission_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenAssistant}
            className="gap-2"
          >
            <MessageSquare className="h-4 w-4" />
            Assistant
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="h-8 w-8"
                aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {theme === 'dark' ? 'Light mode' : 'Dark mode'}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </header>
  )
}
